import { useState } from 'react';
import { Surface, Badge, Button, Grid, PageHeader, Input, Select, Modal } from '../../ui/components';
import { Plus, Eye, Edit, CircleDot } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)', fontSize: 'var(--lk-text-sm)' }}>
    <span style={{ color: 'var(--lk-text-tertiary)', fontWeight: 500 }}>{label}</span>
    <span style={{ color: 'var(--lk-text-primary)' }}>{value}</span>
  </div>
);

const LOAN_PRODUCTS = [
  'Conventional', 'FHA', 'VA', 'USDA', 'Jumbo', 'Bank Statement', 'DSCR', 'Interest-Only',
];

const mockUnderwriters = [
  {
    id: '1',
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@loanknow.com',
    license_number: 'UW-2024-001',
    products: ['Conventional', 'FHA', 'VA'],
    capacity: 25,
    current_pipeline: 18,
    is_available: true,
    years_experience: 8,
    metrics: {
      avg_time_per_loan: 2.3,
      conditions_added: 156,
      ai_conditions_used: 89,
      customer_messages: 342,
      loans_funded: 145,
      total_earnings: 72500,
    },
  },
  {
    id: '2',
    name: 'Michael Chen',
    email: 'michael.chen@loanknow.com',
    license_number: 'UW-2024-002',
    products: ['Jumbo', 'Bank Statement', 'DSCR'],
    capacity: 20,
    current_pipeline: 15,
    is_available: true,
    years_experience: 12,
    metrics: {
      avg_time_per_loan: 1.8,
      conditions_added: 203,
      ai_conditions_used: 145,
      customer_messages: 487,
      loans_funded: 198,
      total_earnings: 99000,
    },
  },
];

const AdminUnderwriters = () => {
  const [underwriters] = useState(mockUnderwriters);
  const [selectedUW, setSelectedUW] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const handleViewDetails = (uw) => {
    setSelectedUW(uw);
    setDetailsOpen(true);
  };

  const utilizationPercent = (uw) => ((uw.current_pipeline / uw.capacity) * 100).toFixed(0);

  return (
    <div>
      <PageHeader
        title="Underwriters"
        description="Manage underwriter team and capacity"
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setAddOpen(true)}>
            Add Underwriter
          </Button>
        }
      />

      {/* Stats */}
      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Underwriters</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{underwriters.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Available Now</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>
            {underwriters.filter(u => u.is_available).length}
          </div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Capacity</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>
            {underwriters.reduce((sum, u) => sum + u.capacity, 0)}
          </div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Current Pipeline</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>
            {underwriters.reduce((sum, u) => sum + u.current_pipeline, 0)}
          </div>
        </Surface>
      </Grid>

      {/* Underwriters Table */}
      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Underwriter</th>
                <th>License</th>
                <th>Products</th>
                <th>Capacity</th>
                <th>Pipeline</th>
                <th>Utilization</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {underwriters.map((uw) => {
                const util = utilizationPercent(uw);
                const barColor = util >= 90 ? 'var(--lk-danger-500)' : util >= 70 ? 'var(--lk-warning-500)' : 'var(--lk-success-500)';
                return (
                  <tr key={uw.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 'var(--lk-radius-full)', backgroundColor: 'var(--lk-primary-100)', color: 'var(--lk-primary-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 'var(--lk-text-xs)' }}>
                          {uw.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500 }}>{uw.name}</div>
                          <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>{uw.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--lk-text-xs)' }}>{uw.license_number}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {uw.products.slice(0, 2).map(p => (
                          <Badge key={p} variant="neutral">{p}</Badge>
                        ))}
                        {uw.products.length > 2 && (
                          <Badge variant="neutral">+{uw.products.length - 2}</Badge>
                        )}
                      </div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{uw.capacity}</td>
                    <td style={{ fontWeight: 600 }}>{uw.current_pipeline}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                        <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: 'var(--lk-gray-100)', minWidth: 60 }}>
                          <div style={{ height: '100%', width: `${util}%`, backgroundColor: barColor, borderRadius: 3, transition: 'width 0.4s ease' }} />
                        </div>
                        <span style={{ fontSize: 'var(--lk-text-xs)', fontWeight: 500, minWidth: 32 }}>{util}%</span>
                      </div>
                    </td>
                    <td>
                      {uw.is_available ? (
                        <Badge variant="success" dot>Available</Badge>
                      ) : (
                        <Badge variant="neutral">Offline</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Button size="sm" variant="ghost" icon={Eye} onClick={() => handleViewDetails(uw)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Surface>

      {/* Details Modal */}
      <Modal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title="Underwriter Details"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDetailsOpen(false)}>Close</Button>
            <Button variant="primary" icon={Edit}>Edit</Button>
          </>
        }
      >
        {selectedUW && (
          <>
            <Grid cols={2} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
              <Surface padding="var(--lk-space-5)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Profile</div>
                <DetailRow label="Name" value={selectedUW.name} />
                <DetailRow label="Email" value={selectedUW.email} />
                <DetailRow label="License" value={selectedUW.license_number} />
                <DetailRow label="Experience" value={`${selectedUW.years_experience} years`} />
              </Surface>
              <Surface padding="var(--lk-space-5)">
                <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Capacity</div>
                <DetailRow label="Max Capacity" value={selectedUW.capacity} />
                <DetailRow label="Current Pipeline" value={selectedUW.current_pipeline} />
                <DetailRow label="Utilization" value={`${utilizationPercent(selectedUW)}%`} />
                <DetailRow label="Status" value={
                  <Badge variant={selectedUW.is_available ? 'success' : 'neutral'}>
                    {selectedUW.is_available ? 'Available' : 'Offline'}
                  </Badge>
                } />
              </Surface>
            </Grid>

            <Surface padding="var(--lk-space-5)" style={{ marginBottom: 'var(--lk-space-4)' }}>
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Specializations</div>
              <div style={{ display: 'flex', gap: 'var(--lk-space-2)', flexWrap: 'wrap' }}>
                {selectedUW.products.map(p => (
                  <Badge key={p} variant="primary">{p}</Badge>
                ))}
              </div>
            </Surface>

            <Surface padding="var(--lk-space-5)">
              <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-4)' }}>Performance Metrics</div>
              <Grid cols={3} gap="var(--lk-space-4)">
                <div style={{ textAlign: 'center' }}>
                  <div className="lk-overline" style={{ marginBottom: 4 }}>Avg Time/Loan</div>
                  <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700 }}>{selectedUW.metrics.avg_time_per_loan} days</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div className="lk-overline" style={{ marginBottom: 4 }}>Loans Funded</div>
                  <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700 }}>{selectedUW.metrics.loans_funded}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div className="lk-overline" style={{ marginBottom: 4 }}>Total Earnings</div>
                  <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700 }}>${selectedUW.metrics.total_earnings.toLocaleString()}</div>
                </div>
              </Grid>
            </Surface>
          </>
        )}
      </Modal>

      {/* Add Modal */}
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Underwriter"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button variant="primary">Add Underwriter</Button>
          </>
        }
      >
        <Input label="Full Name" placeholder="Enter name" />
        <Input label="Email" type="email" placeholder="Enter email" style={{ marginTop: 'var(--lk-space-3)' }} />
        <Input label="License Number" placeholder="Enter license number" style={{ marginTop: 'var(--lk-space-3)' }} />
        <Input label="Capacity" type="number" defaultValue={25} style={{ marginTop: 'var(--lk-space-3)' }} />
        <Select label="Products" multiple style={{ marginTop: 'var(--lk-space-3)' }}>
          {LOAN_PRODUCTS.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </Select>
      </Modal>

      <FloatingOwenAssistant context="admin-underwriters" />
      <FloatingKnowledge customContent={{
        label: 'Underwriters',
        title: 'Underwriter management',
        body: 'Manage underwriter accounts and monitor capacity.',
        bullets: ['Track capacity utilization', 'Monitor pipeline distribution', 'Manage availability status'],
        resources: [{ label: 'Underwriter guidelines', href: '#' }],
      }} />
    </div>
  );
};

export default AdminUnderwriters;
